import $ from 'jquery';
import includes from 'lodash/includes';
import throttle from 'lodash/throttle';
import registerJQueryPlugin from '../lib/register-jquery-plugin';

/**
 * Triggers the scroll event to move to the top of the page.
 * @param {jQuery} $body - Body element
 * @param {jQuery} $screen - The main viewport of the site
 */
const anchorPage = ($body, $screen) => {
  const focusActive = $('html').hasClass('focus-active');
  $screen.animate({ scrollTop: 0 }, 'slow', () => {
    if(focusActive) {
      $('#main-content').focus();
    } else {
      $body
        .attr('tabIndex', -1)
        .focus();
    }
  });
}

/**
 * Simple helper used to set and resolve the visibility state and convert to a
 * promise for ease of interacting with.
 * @param {jQuery} $el - Target element
 * @param {string} visibilityState - The state in which the element should be
 * visible, translates to the method on the jQuery object
 * @param {Object} data - Data used to resolve the promise
 * @returns {jQuery.Deferred} When the visibility action has completed
 */
const resolveVisibilityState = ($el, visibilityState) => {
  if (!includes(['fadeIn', 'fadeOut'], visibilityState)) {
    return $.Deferred().reject(new Error(`Invalid visibility state of ${visibilityState}`));
  }

  const $deferred = $.Deferred();

  $el[visibilityState](() => {
    $deferred.resolve();
  });

  return $deferred.promise();
};

/**
 * Handles the visibility of the target element, i.e. when it is hidden or
 * visible.
 * @param {jQuery} - Target element
 * @param {jQuery} - window object
 * @param {number} - scrollThreshold - The position in which the target element
 * should be visible
 * @param {boolean} - Whether the target element initiated the scrolling and if
 * it's currently scrolling
 * @returns {jQuery.Deferred} When the visibility state has completed.
 */
const handleVisibility = ($el, $window, scrollThreshold, isScrolling) => {
  if (isScrolling) {
    return resolveVisibilityState($el, 'fadeOut');
  }

  const scrollPos = $window.scrollTop();
  if (scrollPos > scrollThreshold) {
    return resolveVisibilityState($el, 'fadeIn');
  }

  return resolveVisibilityState($el, 'fadeOut');
};

/**
 * Scrolls the page to the top while providing additional visibility behavior
 * to the target element.
 * @param {HTMLElement} el - Element used for targeting the browser to scroll to top
 * @param {Object} [options={}]
 * @param {HTMLElement|string} [options.window=window] = The window element/selector
 * @param {string} [options.screen='HTMLAllCollection,body'] = The visible screen
 * @param {number} [options.wait=100] = The time to wait upon throttling the
 * scroll event
 * @param {number} [options.scrollThreshold=250] - The position in which the
 * target element should be visible
 */
const scrollToTop = (el, options = {}) => {
  const $el = $(el);
  const {
    window: win = window,
    screen: screenSelector = 'html,body',
    content = 'body',
    wait = 100,
    scrollThreshold = 250,
  } = Object.assign({}, options, $el.data());
  const $window = $(win);
  const $screen = $(screenSelector);
  const $body = $(content);
  let isScrolling = false;
  if ($el.length) {
    $el.on('click', (evt) => {
      anchorPage($body, $screen, evt);
      const focusActive = $('html').hasClass('focus-active');
      if (focusActive) {
        isScrolling = false;
      } else {
        isScrolling = true;
      }
    });

    $window.on('scroll', throttle(() => {
      handleVisibility($el, $window, scrollThreshold, isScrolling).then(() => {
        if (isScrolling && $window.scrollTop() === 0) {
          isScrolling = false;
        }
      });
    }, wait));
  };
}

export default registerJQueryPlugin('scrollToTop', scrollToTop);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/scroll-to-top.js